import { createSlice } from "@reduxjs/toolkit";
import { rgsPlay, setFeatureSpinComplete } from "./spinSlice";
import { initializeGame } from "./gameSettingsSlice";
import { setSceneTransitionActive } from "./animationsSlice";
import { GAME_LAYOUT } from "../../settings";
import { openHoldAndWinResumePopup } from "./userInterfaceSlice";
import { FEATURES_CONFIG } from "../../featuresConfig";

// Initial state for feature game settings
const initialState = {
  holdAndWinActive: false, // Track if hold and win feature game is currently active
  royalCollectActive: false, // Track if Royal Collect feature is currently active
  respinsLeft: 0, // Track the number of respins left (displayed value)
  pendingRespinsLeft: null, // Store pending respins from API (updated after spin completes)
  initialReels: [], // Initial reels from trigger round (set once, never changes during feature)
  triggerRound: null, // Original trigger round (coins + regular symbols) to return to after feature ends
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
    storage: null, // Store coin_multipliers, progress_bar, etc.
  },
  progressBarCount: 0, // Track total coins collected for progress bar
  holdAndWinSymbolsReady: false, // Track if all Hold and Win symbols are initialized and ready
  readySymbolsCount: 0, // Count of symbols that have reported ready (out of 20 total: 5 reels × 4 symbols)
  holdAndWinReelsReady: false, // Track if all Hold and Win base reels have exposed their refs via imperative handles
  readyReelsCount: 0, // Count of base reels that have reported ready (out of 5 total reels)
  holdAndWinOverlayReelsReady: false, // Track if all Hold and Win overlay reels have exposed their refs
  readyOverlayReelsCount: 0, // Count of overlay reels that have reported ready (out of 5 total overlay reels)
  isHoldAndWinIntroReload: false, // Flag: reloading right after trigger (before any respins)
  isHoldAndWinMidFeatureReload: false, // Flag: reloading mid-feature (after at least one respin)
  isHoldAndWinCompletionReload: false, // Flag: reloading after completion (0 respins left)
  completionDataSaved: false, // Flag: track if completion data has been saved to prevent duplicates

  // Free Spin state
  freeSpinTriggered: false, // True when 3+ scatters detected — blocks spin button
  freeSpinCount: 0, // Number of free spin rounds awarded this trigger (from API freeSpinsAwarded)
  freeSpinIndex: 0, // Current free spin round index (0-based from API, displayed as +1)
  freeSpinTotal: 0, // Cumulative total win during free spins
  freeSpinActive: false, // True while actively playing through free spin rounds
  freeSpinSpinning: false, // True during a single free spin round (cover → API → reveal)
  freeSpinComplete: true, // False during round, true when last mask reveal finishes

  // Royal Collect state
  royalCollectTriggered: false, // True when trigger detected (coin+royal combo) — blocks spin button
  royalCollectInitialReels: [], // Initial reels from trigger round (coins + blanks)
  royalCollectCurrentRound: {
    reels: [],
    wins: [],
    win: 0,
    storage: null,
  },
  royalCollectRespinsLeft: 0, // Respins remaining (display value)
  royalCollectPendingRespinsLeft: null, // API respins value, applied when first coin lands
  royalCollectSymbolsReady: false, // All RC symbol controllers initialized
  royalCollectReadySymbolsCount: 0, // Count of symbols that have reported ready
  royalCollectReelsReady: false, // All RC base reels have exposed refs
  royalCollectReadyReelsCount: 0, // Count of reels that have reported ready
  royalCollectOverlayReelsReady: false, // All RC overlay reels have exposed refs
  royalCollectReadyOverlayReelsCount: 0, // Count of overlay reels that have reported ready
};

// Feature game settings slice
const featureGameSettingsSlice = createSlice({
  name: "featureGameSettings",
  initialState,
  reducers: {
    // Set hold and win active state
    setHoldAndWinActive: (state, action) => {
      state.holdAndWinActive = action.payload;
      // Reset pending respins and completion flag when HoldAndWin becomes inactive
      if (!action.payload) {
        state.pendingRespinsLeft = null;
        state.completionDataSaved = false;
      }
    },
    // Set Royal Collect active state
    setRoyalCollectActive: (state, action) => {
      state.royalCollectActive = action.payload;
    },
    setRespinsLeft: (state, action) => {
      state.respinsLeft = action.payload;
    },
    setProgressBarCount: (state, action) => {
      state.progressBarCount = action.payload;
    },
    // Report that a symbol is ready
    reportSymbolReady: (state) => {
      state.readySymbolsCount += 1;

      // All 20 symbols ready (5 reels × 4 symbols)
      if (state.readySymbolsCount >= 20) {
        state.holdAndWinSymbolsReady = true;
      }
    },
    // Report that a base reel's imperative handle is ready
    reportReelReady: (state) => {
      state.readyReelsCount += 1;

      // All 5 base reels ready
      if (state.readyReelsCount >= 5) {
        state.holdAndWinReelsReady = true;
      }
    },
    // Report that an overlay reel's imperative handle is ready
    reportOverlayReelReady: (state) => {
      state.readyOverlayReelsCount += 1;

      // All 5 overlay reels ready
      if (state.readyOverlayReelsCount >= 5) {
        state.holdAndWinOverlayReelsReady = true;
      }
    },
    // Reset symbol and reel readiness (when Hold and Win deactivates or game reloads)
    resetSymbolReadiness: (state) => {
      state.holdAndWinSymbolsReady = false;
      state.readySymbolsCount = 0;
      state.holdAndWinReelsReady = false;
      state.readyReelsCount = 0;
      state.holdAndWinOverlayReelsReady = false;
      state.readyOverlayReelsCount = 0;
    },
    // Clear completion reload flag (called when user closes dialog and localStorage is cleared)
    clearHoldAndWinCompletionReload: (state) => {
      state.isHoldAndWinCompletionReload = false;
    },

    // Set free spin active state (true when free spin rounds begin)
    setFreeSpinActive: (state, action) => {
      state.freeSpinActive = action.payload;
    },

    // Reset free spin triggered state
    setFreeSpinTriggered: (state, action) => {
      state.freeSpinTriggered = action.payload;
    },

    // Free spin round lifecycle (parallels isSpinning/spinComplete)
    setFreeSpinSpinning: (state, action) => {
      state.freeSpinSpinning = action.payload;
      if (action.payload) {
        state.freeSpinComplete = false;
      }
    },
    setFreeSpinComplete: (state, action) => {
      state.freeSpinComplete = action.payload;
      if (action.payload) {
        state.freeSpinSpinning = false;
      }
    },

    // Complete reset of all free spin state back to initial values
    // Called from exitFreeSpinReveal after outro dialog closes
    resetFreeSpinState: (state) => {
      state.freeSpinTriggered = false;
      state.freeSpinCount = 0;
      state.freeSpinIndex = 0;
      state.freeSpinTotal = 0;
      state.freeSpinActive = false;
      state.freeSpinSpinning = false;
      state.freeSpinComplete = true;
    },

    // Set Royal Collect triggered state (true when trigger symbols detected)
    setRoyalCollectTriggered: (state, action) => {
      state.royalCollectTriggered = action.payload;
    },

    // Royal Collect readiness reporting
    reportRoyalCollectSymbolReady: (state) => {
      state.royalCollectReadySymbolsCount += 1;
      const totalSymbols = GAME_LAYOUT.REEL_COUNT * GAME_LAYOUT.SYMBOLS_VISIBLE;
      if (state.royalCollectReadySymbolsCount >= totalSymbols) {
        state.royalCollectSymbolsReady = true;
      }
    },
    reportRoyalCollectReelReady: (state) => {
      state.royalCollectReadyReelsCount += 1;
      if (state.royalCollectReadyReelsCount >= GAME_LAYOUT.REEL_COUNT) {
        state.royalCollectReelsReady = true;
      }
    },
    reportRoyalCollectOverlayReelReady: (state) => {
      state.royalCollectReadyOverlayReelsCount += 1;
      if (state.royalCollectReadyOverlayReelsCount >= GAME_LAYOUT.REEL_COUNT) {
        state.royalCollectOverlayReelsReady = true;
      }
    },
    resetRoyalCollectReadiness: (state) => {
      state.royalCollectSymbolsReady = false;
      state.royalCollectReadySymbolsCount = 0;
      state.royalCollectReelsReady = false;
      state.royalCollectReadyReelsCount = 0;
      state.royalCollectOverlayReelsReady = false;
      state.royalCollectReadyOverlayReelsCount = 0;
    },
    // Apply pending RC respins immediately (dispatched when first coin lands)
    applyRoyalCollectPendingRespins: (state) => {
      if (state.royalCollectPendingRespinsLeft !== null) {
        state.royalCollectRespinsLeft = state.royalCollectPendingRespinsLeft;
        state.royalCollectPendingRespinsLeft = null;
      }
    },
    // Complete reset of all Royal Collect state back to initial values
    // Called from scene transition midpoint after feature ends
    resetRoyalCollectState: (state) => {
      state.royalCollectTriggered = false;
      state.royalCollectInitialReels = [];
      state.royalCollectCurrentRound = {
        reels: [],
        wins: [],
        win: 0,
        storage: null,
      };
      state.royalCollectRespinsLeft = 0;
      state.royalCollectPendingRespinsLeft = null;
      state.triggerRound = null;
      // Reset readiness so components re-initialize
      state.royalCollectSymbolsReady = false;
      state.royalCollectReadySymbolsCount = 0;
      state.royalCollectReelsReady = false;
      state.royalCollectReadyReelsCount = 0;
      state.royalCollectOverlayReelsReady = false;
      state.royalCollectReadyOverlayReelsCount = 0;
    },

  },
  extraReducers: (builder) => {
    builder
      // Handle Hold and Win reload from initializeGame
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // React to universal reloadState from RGS
        // RGS has already loaded from localStorage and provides unified state
        if (
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
          result.reloadState?.needsReload &&
          result.reloadState.type === "holdAndWin" &&
          result.reloadState.phase === "completion"
        ) {
          const hnsCompletion = result.reloadState.data;
          
          // Set completion reload flag
          state.isHoldAndWinCompletionReload = true;
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = false;

          // CRITICAL: Activate Hold and Win mode (same as mid-feature reload)
          // This allows collection controller to trigger naturally
          state.holdAndWinActive = true;

          // Restore Hold and Win data (unified structure)
          state.currentRound = hnsCompletion.round;
          // CRITICAL: Use round.reels (final state with all coins)
          state.initialReels = hnsCompletion.round.reels;
          state.progressBarCount = hnsCompletion.progressBar;
          state.respinsLeft = 0; // Already completed (respinsLeft === 0 in storage)
          state.pendingRespinsLeft = null;

          // CRITICAL: Restore trigger round from result.round (loaded from base_game_round storage)
          if (result.round) {
            state.triggerRound = result.round;
          }

          // Reset symbol and reel readiness - they will report when ready
          state.holdAndWinSymbolsReady = false;
          state.readySymbolsCount = 0;
          state.holdAndWinReelsReady = false;
          state.readyReelsCount = 0;
          state.holdAndWinOverlayReelsReady = false;
          state.readyOverlayReelsCount = 0;

          return; // Exit early, don't process other reload scenarios
        }

        // Handle intro/active reload from universal reloadState
        if (
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
          result.reloadState?.needsReload &&
          result.reloadState.type === "holdAndWin" &&
          (result.reloadState.phase === "intro" ||
            result.reloadState.phase === "active")
        ) {
          const reloadData = result.reloadState.data;

          if (result.reloadState.phase === "intro") {
            // Intro reload: Right after trigger (before first respin)
            state.holdAndWinActive = false;
            state.isHoldAndWinIntroReload = true;
            state.isHoldAndWinMidFeatureReload = false;
            state.isHoldAndWinCompletionReload = false;

            // Store respins and progress bar
            state.respinsLeft = reloadData.respinsLeft;
            state.progressBarCount = reloadData.progressBar || 0;

            // Get transformed reels from featureState
            if (result.featureState?.data?.transformedReels) {
              state.initialReels = result.featureState.data.transformedReels;
            }
            if (result.featureState?.data?.currentRound) {
              state.currentRound = result.featureState.data.currentRound;
            }

            // Restore trigger round so WinDisplay can show dual-line "Total Win" during H&W
            if (result.round) {
              state.triggerRound = result.round;
            }
          } else if (result.reloadState.phase === "active") {
            // Active reload: Mid-feature (after at least one respin)
            state.holdAndWinActive = true;
            state.isHoldAndWinIntroReload = false;
            state.isHoldAndWinMidFeatureReload = true;
            state.isHoldAndWinCompletionReload = false;

            // Store respins and progress bar
            state.respinsLeft = reloadData.respinsLeft;
            state.progressBarCount = reloadData.progressBar || 0;

            // Get current screen from featureState
            if (result.featureState?.data?.currentRound) {
              state.initialReels = result.featureState.data.currentRound.reels;
              state.currentRound = result.featureState.data.currentRound;
            }

            // Restore trigger round so WinDisplay can show dual-line "Total Win" during H&W
            if (result.round) {
              state.triggerRound = result.round;
            }

            // Reset readiness flags
            state.holdAndWinSymbolsReady = false;
            state.readySymbolsCount = 0;
            state.holdAndWinReelsReady = false;
            state.readyReelsCount = 0;
            state.holdAndWinOverlayReelsReady = false;
            state.readyOverlayReelsCount = 0;
          }

          return; // Exit early
        }

        // CRITICAL: Check featureState for H&W intro during Free Rounds
        // When H&W triggers during Free Rounds, reloadState.type is 'freeRounds', not 'holdAndWin'
        // But featureState.type will be 'holdAndWin' with phase 'intro'
        if (
          FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
          result.featureState?.type === "holdAndWin" &&
          result.featureState.phase === "intro"
        ) {
          // Intro: Right after trigger (before first respin)
          state.holdAndWinActive = false;
          state.isHoldAndWinIntroReload = true;
          state.isHoldAndWinMidFeatureReload = false;
          state.isHoldAndWinCompletionReload = false;

          // Get data from featureState
          if (result.featureState.data) {
            state.respinsLeft = result.featureState.data.respinsLeft || 3;
            state.progressBarCount = result.featureState.data.progressBar || 0;
            
            if (result.featureState.data.transformedReels) {
              state.initialReels = result.featureState.data.transformedReels;
            }
            if (result.featureState.data.currentRound) {
              state.currentRound = result.featureState.data.currentRound;
            }
          }
          
          return; // Exit early
        }
        
        // If no Hold & Win reload, ensure Hold and Win is inactive
        if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
          // Normal base game - ensure Hold and Win is inactive
          state.holdAndWinActive = false;
          state.respinsLeft = 0;
          state.initialReels = [];
          state.triggerRound = null;
          state.currentRound = {
            reels: [],
            wins: [],
            win: 0,
            storage: null,
          };
          state.progressBarCount = 0;
          state.holdAndWinSymbolsReady = false;
          state.readySymbolsCount = 0;
          state.holdAndWinReelsReady = false;
          state.readyReelsCount = 0;
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = false;
          state.isHoldAndWinCompletionReload = false;
          state.holdAndWinOverlayReelsReady = false;
          state.readyOverlayReelsCount = 0;
        }
      })
      // TODO: Refactor to use universal state format in Phase 6
      // For now, keeping legacy logic for backward compatibility
      // Decrease respins immediately when HoldAndWin spin starts
      .addCase(rgsPlay.pending, (state) => {

        // Clear triggerRound immediately when spinning from base game after H&W ended.
        // This ensures WinDisplay stops showing the dual-line display the instant the
        // next spin is pressed, without waiting for rgsPlay.fulfilled to come back.
        if (!state.holdAndWinActive && state.triggerRound !== null) {
          state.triggerRound = null;
        }

        // Only decrease if HoldAndWin is active and we have respins left
        if (state.holdAndWinActive && state.respinsLeft > 0) {
          // Immediately decrease respins (user paid for this spin)
          state.respinsLeft -= 1;
        }

      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        // CRITICAL: Don't modify state during completion reload
        // The completion reload state must be preserved until dialog is closed
        if (state.isHoldAndWinCompletionReload) {
          return;
        }

        const { featureState, round } = action.payload;

        // Use universal featureState
        if (featureState && featureState.type === "holdAndWin") {
          if (featureState.phase === "intro") {
            // Trigger round: Store transformed reels for Hold & Win rendering
            if (featureState.data.transformedReels) {
              state.initialReels = featureState.data.transformedReels;
            }

            // CRITICAL: Store the original trigger round (with regular symbols)
            // This is the round we need to return to after Hold and Win ends
            if (round) {
              state.triggerRound = round;
            }

            // Set initial respins value immediately (this is the starting value)
            if (featureState.data.respinsLeft !== undefined) {
              state.respinsLeft = featureState.data.respinsLeft;
              state.pendingRespinsLeft = null; // No pending value for trigger round
            }

            // Store current round
            if (featureState.data.currentRound) {
              state.currentRound = featureState.data.currentRound;
            }
          } else if (featureState.phase === "active") {
            // Active Hold & Win: Store current round
            if (featureState.data.currentRound) {
              state.currentRound = featureState.data.currentRound;
            }

            // Store pending respins value (will update display after spin completes)
            if (featureState.data.respinsLeft !== undefined) {
              state.pendingRespinsLeft = featureState.data.respinsLeft;
            }
          }
        } else if (
          FEATURES_CONFIG.FREE_SPINS.ENABLED &&
          featureState &&
          featureState.type === "freeSpins"
        ) {
          // Free spin trigger detected: 3+ scatters
          if (featureState.phase === "intro") {
            // Reset stale state from previous free spin game BEFORE setting new values
            // Without this, freeSpinIndex from the last game persists and
            // handleFreeSpinSpin immediately thinks it's the last round
            state.freeSpinIndex = 0;
            state.freeSpinTotal = 0;
            state.freeSpinTriggered = true;
            state.freeSpinCount = featureState.data?.freeSpinCount || 0;
          }
        } else if (
          FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
          featureState &&
          featureState.type === "royalCollect"
        ) {
          // Royal Collect trigger detected
          if (featureState.phase === "intro") {
            state.royalCollectTriggered = true;

            // Store transformed reels for Royal Collect rendering
            if (featureState.data.transformedReels) {
              state.royalCollectInitialReels = featureState.data.transformedReels;
            }

            // Set initial respins
            if (featureState.data.respinsLeft !== undefined) {
              state.royalCollectRespinsLeft = featureState.data.respinsLeft;
            }

            // Store current round
            if (featureState.data.currentRound) {
              state.royalCollectCurrentRound = featureState.data.currentRound;
            }

            // Store trigger round
            if (round) {
              state.triggerRound = round;
            }

          } else if (featureState.phase === "active") {
            // Active Royal Collect: Store current round
            if (featureState.data.currentRound) {
              state.royalCollectCurrentRound = featureState.data.currentRound;
            }

            // Decrement on valid API response (spin confirmed by server)
            if (state.royalCollectRespinsLeft > 0) {
              state.royalCollectRespinsLeft -= 1;
            }

            // Store pending respins value (will update display after spin completes)
            // Mirrors H&W pattern: fulfilled decrements immediately,
            // setFeatureSpinComplete applies the API's authoritative value.
            if (featureState.data.respinsLeft !== undefined) {
              state.royalCollectPendingRespinsLeft = featureState.data.respinsLeft;
            }
          }
        } else if (
          featureState &&
          featureState.type === "bonus"
        ) {
          // Pot Collection — handled entirely by potCollectionSlice.
          // No action needed here, but prevent the else branch from
          // resetting featureGame state on pot collection triggers.
        } else {
          // Regular spin: Reset everything to empty/default
          state.initialReels = [];
          state.triggerRound = null;
          state.currentRound = {
            reels: [],
            wins: [],
            win: 0,
          };
          state.pendingRespinsLeft = null;
          // Reset triggers on regular spins
          state.freeSpinTriggered = false;
          state.royalCollectTriggered = false;
        }

        // Update free spin index from round data (present on every free spin response)
        if (round?.freeSpinIndex !== undefined) {
          state.freeSpinIndex = round.freeSpinIndex;
        }
        // Update cumulative total win from round data (present on every free spin response)
        if (round?.roundTotalWin !== undefined) {
          state.freeSpinTotal = round.roundTotalWin;
        }
      })
      // Update respins from pending value when spin completes
      .addCase(setFeatureSpinComplete, (state, action) => {
        // Handle both boolean and object payloads for backward compatibility
        const isComplete = typeof action.payload === 'boolean' ? action.payload : action.payload?.complete;

        // Only update if spin completed and we have pending value
        if (isComplete === true && state.pendingRespinsLeft !== null) {
          state.respinsLeft = state.pendingRespinsLeft;
          state.pendingRespinsLeft = null; // Clear pending value
        }

        // Royal Collect: pending respins are applied by applyRoyalCollectPendingRespins
        // (dispatched from handleSymbolStopComplete when first coin lands)


        // NOTE: Completion data saving is now handled by RGS.saveGameState()
        // This ensures universal storage management across all games
      })
      // Clear reload flags when scene transition COMPLETES (not when it starts)
      // This ensures flags stay active during transition so animations trigger while screen is black
      .addCase(setSceneTransitionActive, (state, action) => {
        // Only clear when transition is ENDING (false)
        if (action.payload === false) {
          state.isHoldAndWinIntroReload = false;
          state.isHoldAndWinMidFeatureReload = false;
          state.isHoldAndWinCompletionReload = false;
        }
      })
      // Also clear reload flags when Hold and Win reload popup is opened (mid-feature reload path)
      .addCase(openHoldAndWinResumePopup, (state) => {
        state.isHoldAndWinIntroReload = false;
        state.isHoldAndWinMidFeatureReload = false;
        state.isHoldAndWinCompletionReload = false;
      });
  },
});

// Export actions for use in components or other slices
export const {
  setHoldAndWinActive,
  setRoyalCollectActive,
  setRespinsLeft,
  setProgressBarCount,
  reportSymbolReady,
  reportReelReady,
  reportOverlayReelReady,
  resetSymbolReadiness,
  clearHoldAndWinCompletionReload,
  setFreeSpinActive,
  setFreeSpinTriggered,
  setFreeSpinSpinning,
  setFreeSpinComplete,
  resetFreeSpinState,
  setRoyalCollectTriggered,
  reportRoyalCollectSymbolReady,
  reportRoyalCollectReelReady,
  reportRoyalCollectOverlayReelReady,
  resetRoyalCollectReadiness,
  applyRoyalCollectPendingRespins,
  resetRoyalCollectState,
} = featureGameSettingsSlice.actions;

// Export selectors
export const selectHoldAndWinActive = (state) =>
  state.featureGame.holdAndWinActive;

export const selectRoyalCollectActive = (state) =>
  state.featureGame.royalCollectActive;

export const selectHoldAndWinInitialReels = (state) =>
  state.featureGame.initialReels;

export const selectHoldAndWinCurrentRound = (state) =>
  state.featureGame.currentRound;

export const selectHoldAndWinCoinMultipliers = (state) =>
  state.featureGame.currentRound?.storage?.coin_multipliers || null;

export const selectRespinsLeft = (state) => state.featureGame.respinsLeft;

export const selectProgressBarCount = (state) =>
  state.featureGame.progressBarCount;

export const selectHoldAndWinSymbolsReady = (state) =>
  state.featureGame.holdAndWinSymbolsReady;

export const selectHoldAndWinReelsReady = (state) =>
  state.featureGame.holdAndWinReelsReady;

export const selectHoldAndWinOverlayReelsReady = (state) =>
  state.featureGame.holdAndWinOverlayReelsReady;

export const selectIsHoldAndWinIntroReload = (state) =>
  state.featureGame.isHoldAndWinIntroReload;

export const selectIsHoldAndWinMidFeatureReload = (state) =>
  state.featureGame.isHoldAndWinMidFeatureReload;

export const selectIsHoldAndWinCompletionReload = (state) =>
  state.featureGame.isHoldAndWinCompletionReload;

// Free Spins selectors
export const selectFreeSpinTriggered = (state) =>
  state.featureGame.freeSpinTriggered;

export const selectFreeSpinActive = (state) =>
  state.featureGame.freeSpinActive;

export const selectFreeSpinCount = (state) =>
  state.featureGame.freeSpinCount;

export const selectFreeSpinIndex = (state) =>
  state.featureGame.freeSpinIndex;

export const selectFreeSpinSpinning = (state) =>
  state.featureGame.freeSpinSpinning;

export const selectFreeSpinComplete = (state) =>
  state.featureGame.freeSpinComplete;

export const selectFreeSpinTotal = (state) =>
  state.featureGame.freeSpinTotal;

// Royal Collect selectors
export const selectRoyalCollectTriggered = (state) =>
  state.featureGame.royalCollectTriggered;

export const selectRoyalCollectInitialReels = (state) =>
  state.featureGame.royalCollectInitialReels;

export const selectRoyalCollectCurrentRound = (state) =>
  state.featureGame.royalCollectCurrentRound;

export const selectRoyalCollectRespinsLeft = (state) =>
  state.featureGame.royalCollectRespinsLeft;

export const selectRoyalCollectSymbolsReady = (state) =>
  state.featureGame.royalCollectSymbolsReady;

export const selectRoyalCollectReelsReady = (state) =>
  state.featureGame.royalCollectReelsReady;

export const selectRoyalCollectOverlayReelsReady = (state) =>
  state.featureGame.royalCollectOverlayReelsReady;

// Export the reducer to integrate into the Redux store
export default featureGameSettingsSlice.reducer;
